/**
 * Allowlist matching for external post-auth redirect targets
 * (`ALLOWED_EXTERNAL_REDIRECT`). Security-critical: a match means the app will
 * navigate the user's browser to that URL with an auth token attached.
 *
 * The predecessor of this module matched with `url.startsWith(pattern)` plus a
 * `*` → `[^/]+` regex, which let crafted URLs smuggle the allowed host into
 * their query/fragment/userinfo (`https://evil.com?x=.starzero.ai/`) and pass.
 * This implementation parses the candidate with `new URL()` and compares
 * components (scheme, hostname, port, path prefix) individually, so those
 * tricks fail structurally: everything after `?`/`#`/`@` never reaches the
 * hostname comparison.
 *
 * Pattern syntax (the env var format, unchanged):
 *
 *   https://chat.starzero.ai/     exact host, default port, any path
 *   https://*.starzero.ai/        any subdomain (one or more labels), NOT the apex
 *   https://*.starzero.ai:*  (+ trailing slash)   same, any port
 *   https://host/app/             path must start with /app/
 *
 * Entries must end with `/`; `*` is only valid as the entire leftmost host
 * label or as the whole port. Scheme and host match case-insensitively, the
 * path prefix case-sensitively.
 *
 * Pure module (no DOM, no store imports) so `npx tsx` can run its tests.
 */

export type AllowedRedirectPattern = {
  /** Lowercase scheme without the trailing colon, e.g. 'https'. */
  scheme: string;
  /** Lowercase hostname; for wildcard patterns, the base the subdomains hang off. */
  host: string;
  /** True when the entry's host was `*.<host>`. */
  wildcardHost: boolean;
  /** null = scheme-default port only; '*' = any port; otherwise exact digits. */
  port: string | null;
  /** Prefix the candidate's pathname must start with; begins and ends with '/'. */
  pathPrefix: string;
};

// Patterns can't go through `new URL()` themselves (`*` hosts and `:*` ports
// don't parse), so they get a stricter hand-rolled grammar instead.
const PATTERN_RE = /^(https?):\/\/([a-z0-9*.-]+)(?::(\d+|\*))?(\/.*)$/i;

const DEFAULT_PORTS: Record<string, string> = { https: '443', http: '80' };

function warnDropped(entry: string, reason: string) {
  // Build-config problem, not a runtime error — warn (not error) so a
  // misconfigured env var doesn't create a Sentry event on every page load.
  console.warn(`ALLOWED_EXTERNAL_REDIRECT: ignoring entry "${entry}": ${reason}`);
}

export function parseAllowedRedirectPatterns(raw: string): AllowedRedirectPattern[] {
  const patterns: AllowedRedirectPattern[] = [];
  for (const entry of raw.split(',').map((x) => x.trim())) {
    if (!entry) {
      continue;
    }
    if (!entry.endsWith('/')) {
      warnDropped(entry, 'must end with a slash to avoid open-redirect prefix matches');
      continue;
    }
    const match = PATTERN_RE.exec(entry);
    if (!match) {
      warnDropped(entry, 'not of the form http(s)://host[:port]/path');
      continue;
    }
    const [, rawScheme, rawHost, rawPort, pathPrefix] = match;
    const scheme = rawScheme.toLowerCase();
    const wildcardHost = rawHost.startsWith('*.');
    const host = (wildcardHost ? rawHost.slice(2) : rawHost).toLowerCase();
    if (!host || host.includes('*')) {
      warnDropped(entry, '"*" is only supported as the entire leftmost host label');
      continue;
    }
    let port: string | null = rawPort ?? null;
    if (port && port !== '*' && port === DEFAULT_PORTS[scheme]) {
      // `new URL()` normalizes an explicit default port to '' on the candidate
      // side; normalize the pattern side the same way so they still compare.
      port = null;
    }
    patterns.push({ scheme, host, wildcardHost, port, pathPrefix });
  }
  return patterns;
}

function matchesPattern(pattern: AllowedRedirectPattern, url: URL): boolean {
  if (url.protocol !== `${pattern.scheme}:`) {
    return false;
  }
  // url.hostname is already lowercased and punycode-normalized by the parser,
  // so case tricks and unicode homographs are settled before we compare.
  if (pattern.wildcardHost) {
    // One or more whole labels: chat.starzero.ai and a.b.starzero.ai match
    // *.starzero.ai; the apex starzero.ai needs its own explicit entry. The
    // length check rejects a degenerate empty label ('.starzero.ai').
    if (
      !url.hostname.endsWith(`.${pattern.host}`) ||
      url.hostname.length <= pattern.host.length + 1
    ) {
      return false;
    }
  } else if (url.hostname !== pattern.host) {
    return false;
  }
  // url.port is '' when the URL uses the scheme-default port.
  if (pattern.port === null) {
    if (url.port) {
      return false;
    }
  } else if (pattern.port !== '*' && url.port !== pattern.port) {
    return false;
  }
  // pathname never contains `?` or `#`, so query/fragment smuggling can't
  // reach this comparison.
  return url.pathname.startsWith(pattern.pathPrefix);
}

export function isAllowedExternalRedirect(
  url: string,
  patterns: readonly AllowedRedirectPattern[],
): boolean {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    // Also rejects protocol-relative ('//evil.com') and garbage inputs.
    return false;
  }
  // No legitimate target uses userinfo; rejecting it outright kills the
  // `https://chat.starzero.ai@evil.com/` class.
  if (parsed.username || parsed.password) {
    return false;
  }
  return patterns.some((pattern) => matchesPattern(pattern, parsed));
}
